/**
 * Performance monitoring and optimization utilities
 */
import React from 'react';

// Performance monitoring
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, number[]> = new Map();
  private observers: Map<string, PerformanceObserver> = new Map();

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  // Monitor frame rate
  startFPSMonitoring(callback?: (fps: number) => void): void {
    let frames = 0;
    let lastTime = performance.now();

    const measureFPS = () => {
      frames++;
      const currentTime = performance.now();
      
      if (currentTime >= lastTime + 1000) {
        const fps = Math.round((frames * 1000) / (currentTime - lastTime));
        this.logMetric('fps', fps);
        
        if (callback) callback(fps);
        
        frames = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(measureFPS);
    };
    
    requestAnimationFrame(measureFPS);
  }

  // Monitor paint timing
  startPaintMonitoring(): void {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.logMetric(entry.name, entry.startTime);
        });
      });
      
      observer.observe({ entryTypes: ['paint'] });
      this.observers.set('paint', observer);
    }
  }

  // Monitor long tasks
  startLongTaskMonitoring(): void {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.logMetric('long-task', entry.duration);
        });
      });
      
      try {
        observer.observe({ entryTypes: ['longtask'] });
        this.observers.set('longtask', observer);
      } catch (e) {
        // Long task monitoring not supported - silently ignore
      }
    }
  }

  private logMetric(name: string, value: number): void {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    
    const values = this.metrics.get(name)!;
    values.push(value);
    
    // Keep only last 100 measurements
    if (values.length > 100) {
      values.shift();
    }
  }

  getMetrics(name: string): { avg: number; min: number; max: number } | null {
    const values = this.metrics.get(name);
    if (!values || values.length === 0) return null;
    
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    return { avg, min, max };
  }

  cleanup(): void {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
    this.metrics.clear();
  }
}

// Animation optimization utilities
export class AnimationOptimizer {
  private static rafCallbacks: Map<string, number> = new Map();
  private static tabVisible = true;
  
  static {
    // Monitor tab visibility
    document.addEventListener('visibilitychange', () => {
      AnimationOptimizer.tabVisible = !document.hidden;
    });
  }

  // Optimized requestAnimationFrame with pause on tab hidden
  static requestFrame(callback: FrameRequestCallback, id?: string): number {
    if (!AnimationOptimizer.tabVisible && id) {
      return 0; // Don't run animations when tab is hidden
    }
    
    const rafId = requestAnimationFrame(callback);
    
    if (id) {
      AnimationOptimizer.rafCallbacks.set(id, rafId);
    }
    
    return rafId;
  }

  static cancelFrame(id: string): void {
    const rafId = AnimationOptimizer.rafCallbacks.get(id);
    if (rafId) {
      cancelAnimationFrame(rafId);
      AnimationOptimizer.rafCallbacks.delete(id);
    }
  }

  static isTabVisible(): boolean {
    return AnimationOptimizer.tabVisible;
  }

  // Throttle animations based on performance
  static createThrottledAnimation(
    callback: () => void,
    targetFPS = 60
  ): () => void {
    const targetInterval = 1000 / targetFPS;
    let lastTime = 0;

    return () => {
      const currentTime = performance.now();
      if (currentTime - lastTime >= targetInterval) {
        callback();
        lastTime = currentTime;
      }
    };
  }
}

// Image optimization utilities
export const ImageOptimizer = {
  // Preload critical images
  preloadImages(imagePaths: string[]): Promise<void[]> {
    const promises = imagePaths.map((src) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = src;
      });
    });
    
    return Promise.all(promises);
  },

  // Create optimized background image style
  createOptimizedBackgroundStyle(imagePath: string, gradient?: string): React.CSSProperties {
    return {
      backgroundImage: gradient 
        ? `${gradient}, url('${imagePath}')`
        : `url('${imagePath}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      // Hardware acceleration
      willChange: 'transform',
      transform: 'translateZ(0)',
    };
  },

  // Lazy load image with intersection observer
  createLazyImage(src: string, placeholder?: string): {
    ref: React.RefCallback<HTMLImageElement>;
    src: string;
  } {
    let imageSrc = placeholder || '';
    let imageRef: HTMLImageElement | null = null;

    const ref: React.RefCallback<HTMLImageElement> = (element) => {
      if (element && element !== imageRef) {
        imageRef = element;
        
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              const img = new Image();
              img.onload = () => {
                if (imageRef) {
                  imageRef.src = src;
                }
              };
              img.src = src;
              observer.disconnect();
            }
          },
          { threshold: 0.1 }
        );
        
        observer.observe(element);
      }
    };

    return { ref, src: imageSrc };
  }
};

// CSS containment utility
export const CSSContainment = {
  // Apply containment for better performance
  createContainmentStyle(
    contain: ('layout' | 'style' | 'paint' | 'size')[] = ['layout', 'style', 'paint']
  ): React.CSSProperties {
    return {
      contain: contain.join(' ') as any,
    };
  }
};

// Hook for monitoring component performance
export function usePerformanceMonitoring(componentName: string) {
  React.useEffect(() => {
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      // Performance monitoring - render time tracked
    };
  });
}
